import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { api, ApiError, wsUrl } from '../lib/api';
import { useLang } from '../lib/i18n';
import { dayShort, hhmm, tl, TIERS, FEE_KURUS } from '../lib/format';
import { getState, setState, useStore } from '../lib/store';
import { toast } from '../lib/toast';
import type { MapResponse, Snapshot } from '../lib/types';
import type { CompiledSeat } from '../../../src/domain/layout';
import { STAGE } from '../../../src/domain/layout';
import { ErrorView, Loading } from '../components/AppHeader';
import { Ico } from '../components/brand';

type Code = 'h' | 's' | 'b';
const WC_PATH = 'M-1.5 -6.5 a1.4 1.4 0 1 0 0.01 0 M-1.5 -3.5 v4 h3.5 l1.6 3.8 M-4 -1 a4 4 0 1 0 5.5 5.2';
const SEAT_SCALE = 0.88;
const EMPTY: string[] = [];

/** Live seat states: WebSocket to the session's inventory DO (hibernating), REST fallback while disconnected. */
function useLiveSeats(sid: string, enabled: boolean) {
  const [states, setStates] = useState<Map<string, Code>>(new Map());
  const [mine, setMine] = useState<Snapshot['mine']>(null);
  const [connected, setConnected] = useState(false);
  const [flash, setFlash] = useState<Set<string>>(new Set());
  const rev = useRef(-1);
  const apply = useCallback((s: Snapshot) => {
    const m = new Map<string, Code>();
    s.held.forEach((id) => m.set(id, 'h'));
    s.sold.forEach((id) => m.set(id, 's'));
    s.blocked.forEach((id) => m.set(id, 'b'));
    rev.current = s.rev;
    setStates(m);
  }, []);
  const refresh = useCallback(async () => {
    const s = await api<Snapshot>(`/sessions/${sid}/state`);
    apply(s);
    setMine(s.mine ?? null);
    return s;
  }, [sid, apply]);

  useEffect(() => {
    if (!enabled) return;
    let ws: WebSocket | null = null;
    let closed = false;
    let retry = 0;
    let poll: ReturnType<typeof setInterval> | undefined;
    let ping: ReturnType<typeof setInterval> | undefined;
    refresh().catch(() => {});
    const connect = () => {
      ws = new WebSocket(wsUrl(`/sessions/${sid}/live`));
      ws.onopen = () => {
        retry = 0;
        setConnected(true);
        clearInterval(poll);
        ping = setInterval(() => ws?.readyState === 1 && ws.send('ping'), 25_000);
      };
      ws.onmessage = (ev) => {
        if (ev.data === 'pong') return;
        const msg = JSON.parse(ev.data);
        if (msg.t === 's') apply(msg);
        else if (msg.t === 'd') {
          if (rev.current >= 0 && msg.rev !== rev.current + 1 && msg.rev > rev.current + 1) {
            ws?.send('snap'); // missed a diff → resync
            return;
          }
          rev.current = msg.rev;
          setStates((prev) => {
            const m = new Map(prev);
            for (const [id, c] of msg.c as [string, string][]) {
              if (c === 'a') m.delete(id);
              else m.set(id, c as Code);
            }
            return m;
          });
          const ids = (msg.c as [string, string][]).map((x) => x[0]);
          setFlash((f) => new Set([...f, ...ids]));
          setTimeout(() => setFlash((f) => new Set([...f].filter((x) => !ids.includes(x)))), 1000);
        }
      };
      ws.onclose = () => {
        clearInterval(ping);
        setConnected(false);
        if (closed) return;
        clearInterval(poll);
        poll = setInterval(() => refresh().catch(() => {}), 8000);
        setTimeout(connect, Math.min(15000, 1000 * 2 ** retry++));
      };
    };
    connect();
    return () => {
      closed = true;
      clearInterval(poll);
      clearInterval(ping);
      ws?.close();
    };
  }, [sid, enabled, apply, refresh]);
  return { states, mine, setMine, connected, flash, refresh };
}

export function SeatMap() {
  const { sid = '' } = useParams();
  const nav = useNavigate();
  const { tx, lang } = useLang();
  const en = lang === 'en';
  const [data, setData] = useState<MapResponse | null>(null);
  const [error, setError] = useState<number | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const admission = useStore((s) => s.admission[sid]);
  const selection = useStore((s) => s.selection[sid] || EMPTY);
  const setSelection = (ids: string[]) => setState((s) => ({ ...s, selection: { ...s.selection, [sid]: ids } }));

  useEffect(() => {
    api<MapResponse>(`/sessions/${sid}/map`).then(setData, (e) => setError(e.status || 500));
  }, [sid]);
  const needsQueue = !!data?.session.demand && (!admission || admission.expiresAt < Date.now());
  useEffect(() => {
    if (needsQueue) nav(`/sira/${sid}`, { replace: true });
  }, [needsQueue, nav, sid]);

  const live = useLiveSeats(sid, !!data && !needsQueue);
  const seats = data?.map.seats ?? [];
  const byId = useMemo(() => new Map(seats.map((s) => [s.id, s])), [seats]);
  const mineSet = useMemo(() => new Set(live.mine?.seats ?? []), [live.mine]);

  // resume an existing hold: its seats become the selection
  useEffect(() => {
    if (live.mine && !getState().selection[sid]?.length) setSelection(live.mine.seats);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live.mine]);

  const stateOf = (id: string): 'avail' | 'held' | 'sold' | 'blocked' => {
    const c = live.states.get(id);
    if (!c) return 'avail';
    if (c === 'h' && mineSet.has(id)) return 'avail';
    return c === 'h' ? 'held' : c === 's' ? 'sold' : 'blocked';
  };

  // someone else took a seat I had selected (not yet held by me)
  const holdingRef = useRef<string[]>([]);
  useEffect(() => {
    const lost = selection.filter((id) => {
      if (holdingRef.current.includes(id)) return false;
      const st = stateOf(id);
      return st === 'sold' || st === 'blocked' || (st === 'held' && !mineSet.has(id));
    });
    if (lost.length) {
      setSelection(selection.filter((id) => !lost.includes(id)));
      const s = byId.get(lost[0]!);
      toast(s ? tx(`${short(s, false)} az önce başkası tarafından alındı`, `${short(s, true)} was just taken by someone else`) : tx('Seçtiğin bir koltuk alındı', 'A seat you picked was taken'), 3200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live.states]);

  const short = (s: CompiledSeat, e: boolean) => (s.single ? `${s.row} · ${e ? 'Seat' : 'Koltuk'} ${s.num}` : `${s.sec} · ${s.row} ${s.num}`);
  const tierName = (t: CompiledSeat['tier']) => (en ? TIERS[t].en : TIERS[t].tr);
  const price = (s: CompiledSeat) => data!.prices[s.tier];
  const seatAria = (s: CompiledSeat, st: string, sel: boolean) =>
    `${s.single ? `${s.row} · ${tx('Koltuk', 'Seat')} ${s.num}` : en ? `${s.sec} · Row ${s.row} · ${s.num}` : `${s.sec} · ${s.row} sırası · ${s.num}`}, ${tierName(s.tier)} ${tl(price(s), lang)}${s.wc ? tx(', tekerlekli sandalye alanı', ', wheelchair space') : ''}${s.rv ? tx(', kısıtlı görüş', ', restricted view') : ''}${
      st === 'sold' ? tx(', satıldı', ', sold') : st === 'held' ? tx(', başkası tarafından tutuluyor', ', held by someone else') : st === 'blocked' ? tx(', satışa kapalı', ', not for sale') : ''
    }${sel ? tx(', seçili', ', selected') : ''}`;

  const toggle = (id: string) => {
    const s = byId.get(id);
    if (!s || !data) return;
    const cur = getState().selection[sid] || [];
    if (cur.includes(id)) return setSelection(cur.filter((x) => x !== id));
    const st = stateOf(id);
    if (st === 'sold' || st === 'blocked') return toast(st === 'sold' ? tx('Bu koltuk satıldı', 'This seat is sold') : tx('Bu koltuk satışa kapalı', 'This seat is not for sale'));
    if (st === 'held') return toast(tx('Bu koltuk şu an başka birinin sepetinde. Birkaç dakika içinde boşalabilir.', 'Someone else is holding this seat right now. It may free up in a few minutes.'), 3400);
    if (cur.length >= data.maxSeats) return toast(tx(`Tek siparişte en fazla ${data.maxSeats} koltuk`, `Max ${data.maxSeats} seats per order`));
    if (s.rv) toast(tx('Kısıtlı görüş: sahnenin bir kısmı görünmeyebilir', 'Restricted view: part of the stage may be hidden'), 3000);
    setSelection([...cur, id]);
  };
  const toggleRef = useRef(toggle);
  toggleRef.current = toggle;

  const holdSeats = async () => {
    if (!selection.length || busy) return;
    setBusy(true);
    holdingRef.current = [...selection];
    try {
      const r = await api<{ holdId: string; expiresAt: number; seats: { id: string; tier: CompiledSeat['tier'] }[] }>(`/sessions/${sid}/hold`, {
        method: 'POST',
        json: { seats: selection, admission: admission?.token },
      });
      live.setMine({ holdId: r.holdId, seats: r.seats.map((x) => x.id), expiresAt: r.expiresAt, status: 'active' });
      setState((s) => ({ ...s, hold: { sessionId: sid, holdId: r.holdId, expiresAt: r.expiresAt, seats: r.seats } }));
      nav(`/odeme/${sid}`);
    } catch (e) {
      const err = e as ApiError;
      if (err.status === 409 && err.body.error === 'conflict') {
        const c = (err.body.conflicts as string[]) || [];
        setSelection(selection.filter((id) => !c.includes(id)));
        toast(tx('Bazı koltuklar az önce başkasının sepetine girdi. Seçimini güncelledik.', 'Some seats were just taken. We updated your selection.'), 3600);
        live.refresh().catch(() => {});
      } else if (err.status === 403) nav(`/sira/${sid}`);
      else if (err.status === 429) toast(tx('Çok hızlı denedin. Birkaç saniye bekle.', 'Too many tries. Wait a few seconds.'));
      else if (err.body?.error === 'payment_in_progress') toast(tx('Bu koltuklar için başlamış bir ödemen var.', 'You have a payment in progress for these seats.'));
      else toast(tx('Koltuklar tutulamadı. Tekrar dene.', 'Couldn’t hold the seats. Try again.'));
      holdingRef.current = [];
    } finally {
      setBusy(false);
    }
  };

  /* ---------------- geometry & viewport ---------------- */
  const geo = useMemo(() => {
    if (!data) return null;
    const b = data.map.bbox;
    const ox = b.x0 - 60,
      oy = STAGE.y0 - 30;
    return { ox, oy, W: Math.ceil(b.x1 - b.x0 + 120), H: Math.ceil(b.y1 - oy + 70) };
  }, [data]);

  const vpRef = useRef<HTMLDivElement>(null);
  const mcRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const legendRef = useRef<HTMLDivElement>(null);
  const dockRef = useRef<HTMLDivElement>(null);
  const zoomRef = useRef<HTMLDivElement>(null);
  const hintRef = useRef<HTMLDivElement>(null);
  const m = useRef({ s: 1, tx: 0, ty: 0, fitted: false });
  const apply = () => {
    if (mcRef.current) mcRef.current.style.transform = `translate(${m.current.tx}px,${m.current.ty}px) scale(${m.current.s})`;
  };
  const desk = () => (document.querySelector('.app') as HTMLElement).clientWidth >= 900;
  const fitMap = useCallback(() => {
    const vp = vpRef.current;
    if (!vp || !geo) return;
    const W = vp.clientWidth,
      H = vp.clientHeight,
      d = desk();
    const s = Math.min(W / geo.W, H / geo.H) * (d ? 0.96 : 1);
    m.current = { s, tx: (W - geo.W * s) / 2, ty: d ? (H - geo.H * s) / 2 : 6, fitted: true };
    apply();
  }, [geo]);
  const zoomAt = (f: number, cx: number, cy: number) => {
    const c = m.current;
    const ns = Math.max(0.25, Math.min(3.2, c.s * f)),
      k = ns / c.s;
    c.tx = cx - (cx - c.tx) * k;
    c.ty = cy - (cy - c.ty) * k;
    c.s = ns;
    apply();
  };

  // position the viewport between the top bar/legend and the mobile sheet
  const layout = useCallback(() => {
    const vp = vpRef.current;
    if (!vp || !topRef.current || !legendRef.current) return;
    const d = desk();
    const topH = topRef.current.offsetHeight + legendRef.current.offsetHeight;
    const dockH = d ? 0 : dockRef.current?.offsetHeight ?? 0;
    vp.style.top = topH + 'px';
    vp.style.bottom = dockH + 'px';
    if (zoomRef.current) zoomRef.current.style.bottom = dockH + 16 + 'px';
    if (hintRef.current) {
      hintRef.current.style.bottom = dockH + 16 + 'px';
      if (d) hintRef.current.style.left = 'calc(50% - 200px)';
    }
  }, []);
  useLayoutEffect(() => {
    layout();
    if (!m.current.fitted) fitMap();
  });
  useEffect(() => {
    const t = setTimeout(() => hintRef.current && (hintRef.current.style.opacity = '0'), 3200);
    const r = () => {
      layout();
      fitMap();
    };
    window.addEventListener('resize', r);
    document.fonts?.ready.then(() => layout());
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', r);
    };
  }, [layout, fitMap]);

  // pointer pan / pinch / wheel — ported from the prototype
  useEffect(() => {
    const vp = vpRef.current;
    if (!vp || !geo) return;
    const ptrs = new Map<number, { x: number; y: number }>();
    let moved = 0,
      last: { x: number; y: number } | null = null,
      pinch0: { d: number; s: number } | null = null;
    const down = (ev: PointerEvent) => {
      ptrs.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
      moved = 0;
      last = { x: ev.clientX, y: ev.clientY };
      if (ptrs.size === 2) {
        const [a, b] = [...ptrs.values()] as [{ x: number; y: number }, { x: number; y: number }];
        pinch0 = { d: Math.hypot(a.x - b.x, a.y - b.y), s: m.current.s };
      }
    };
    const move = (ev: PointerEvent) => {
      if (!ptrs.has(ev.pointerId)) return;
      ptrs.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
      if (ptrs.size === 2 && pinch0) {
        const [a, b] = [...ptrs.values()] as [{ x: number; y: number }, { x: number; y: number }];
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        const r = vp.getBoundingClientRect();
        zoomAt((pinch0.s * d) / pinch0.d / m.current.s, (a.x + b.x) / 2 - r.left, (a.y + b.y) / 2 - r.top);
        moved = 99;
        return;
      }
      const dx = ev.clientX - last!.x,
        dy = ev.clientY - last!.y;
      moved += Math.abs(dx) + Math.abs(dy);
      if (moved > 6) {
        vp.classList.add('drag');
        if (!vp.hasPointerCapture(ev.pointerId)) vp.setPointerCapture(ev.pointerId);
        m.current.tx += dx;
        m.current.ty += dy;
        apply();
      }
      last = { x: ev.clientX, y: ev.clientY };
    };
    const up = (ev: PointerEvent) => {
      ptrs.delete(ev.pointerId);
      if (ptrs.size < 2) pinch0 = null;
      vp.classList.remove('drag');
    };
    const wheel = (ev: WheelEvent) => {
      ev.preventDefault();
      const r = vp.getBoundingClientRect();
      zoomAt(ev.deltaY < 0 ? 1.12 : 1 / 1.12, ev.clientX - r.left, ev.clientY - r.top);
    };
    const dbl = (ev: MouseEvent) => {
      const r = vp.getBoundingClientRect();
      zoomAt(1.6, ev.clientX - r.left, ev.clientY - r.top);
    };
    const click = (ev: MouseEvent) => {
      if (moved > 6) return;
      const g = (ev.target as Element).closest('[data-seat]') as SVGGElement | null;
      if (g) toggleRef.current(g.dataset.seat!);
    };
    vp.addEventListener('pointerdown', down);
    vp.addEventListener('pointermove', move);
    vp.addEventListener('pointerup', up);
    vp.addEventListener('pointercancel', up);
    vp.addEventListener('wheel', wheel, { passive: false });
    vp.addEventListener('dblclick', dbl);
    vp.addEventListener('click', click);
    return () => {
      vp.removeEventListener('pointerdown', down);
      vp.removeEventListener('pointermove', move);
      vp.removeEventListener('pointerup', up);
      vp.removeEventListener('pointercancel', up);
      vp.removeEventListener('wheel', wheel);
      vp.removeEventListener('dblclick', dbl);
      vp.removeEventListener('click', click);
    };
  }, [geo]);

  // keyboard: Enter/Space toggles, arrow keys jump to the nearest seat in that direction
  const onKey = (ev: React.KeyboardEvent) => {
    const id = (ev.target as SVGElement).getAttribute?.('data-seat');
    if (!id) return;
    if (ev.key === 'Enter' || ev.key === ' ') {
      ev.preventDefault();
      toggle(id);
      return;
    }
    const dir = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[ev.key];
    if (!dir) return;
    ev.preventDefault();
    const cur = byId.get(id)!;
    let best: CompiledSeat | null = null,
      bestD = Infinity;
    for (const s of seats) {
      if (s.id === id) continue;
      const dx = s.x - cur.x,
        dy = s.y - cur.y;
      const along = dx * dir[0]! + dy * dir[1]!;
      if (along <= 4) continue;
      const across = Math.abs(dx * dir[1]! - dy * dir[0]!);
      const d = along + across * 2.5;
      if (d < bestD) {
        bestD = d;
        best = s;
      }
    }
    if (best) {
      const el = mcRef.current?.querySelector(`[data-seat="${CSS.escape(best.id)}"]`) as SVGGElement | null;
      el?.focus();
      // keep the focused seat on screen
      const vp = vpRef.current!,
        c = m.current,
        g = geo!;
      const sx = (best.x - g.ox) * c.s + c.tx,
        sy = (best.y - g.oy) * c.s + c.ty;
      if (sx < 40 || sy < 40 || sx > vp.clientWidth - 40 || sy > vp.clientHeight - 40) {
        c.tx += vp.clientWidth / 2 - sx;
        c.ty += vp.clientHeight / 2 - sy;
        apply();
      }
    }
  };

  if (error) return <ErrorView msg={tx('Koltuk haritası yüklenemedi.', 'Couldn’t load the seat map.')} action={<button className="btn btn-line" onClick={() => nav('/')}>{tx('Keşfet’e dön', 'Back')}</button>} />;
  if (!data || !geo || needsQueue) return <Loading />;

  const selSet = new Set(selection);
  const n = selection.length;
  const subtotal = selection.reduce((a, id) => a + (byId.get(id) ? price(byId.get(id)!) : 0), 0);
  const total = subtotal + FEE_KURUS * n;
  const counts: Record<string, number> = {};
  for (const s of seats) {
    const k = s.single ? 'Loca' : s.sec;
    if (counts[k] === undefined) counts[k] = 0;
    if (stateOf(s.id) === 'avail') counts[k]++;
  }
  const countsTxt = Object.entries(counts)
    .map(([k, v]) => `${en ? (k === 'Loca' ? 'Boxes' : k === 'Parter' ? 'Stalls' : k === 'Balkon' ? 'Balcony' : k) : k} ${v}`)
    .join(' · ');
  const freeTotal = Object.values(counts).reduce((a, b) => a + b, 0);
  const title = data.event.title;

  const selList = !n ? (
    <p className="muted t-s" style={{ margin: '6px 0 0' }}>
      {tx(
        `Haritadan en fazla ${data.maxSeats} koltuk seçebilirsin. Seçtiğin koltuklar ödemeye geçtiğinde 7 dakika boyunca senin için tutulur.`,
        `Pick up to ${data.maxSeats} seats on the map. They’re held for you for 7 minutes once you continue to payment.`,
      )}
    </p>
  ) : (
    <div className="sel-list">
      {selection.map((id) => {
        const s = byId.get(id);
        if (!s) return null;
        return (
          <div className="sel-item" key={id}>
            <span className="sw" style={{ background: TIERS[s.tier].css }} />
            <div>
              <div className="where">{s.single ? `${s.row} · ${tx('Koltuk', 'Seat')} ${s.num}` : `${en ? (s.sec === 'Parter' ? 'Stalls' : s.sec === 'Balkon' ? 'Balcony' : s.sec) : s.sec} · ${s.row} ${s.num}`}</div>
              <div className="tn">
                {tierName(s.tier)}
                {s.wc ? tx(' · Erişilebilir', ' · Accessible') : ''}
                {s.rv ? tx(' · Kısıtlı görüş', ' · Restricted view') : ''}
              </div>
            </div>
            <span className="pr">{tl(price(s), lang)}</span>
            <button type="button" className="x" onClick={() => toggle(id)} aria-label={`${short(s, en)} ${tx('seçimini kaldır', 'remove')}`}>
              <Ico n="close" cls="ico-s" />
            </button>
          </div>
        );
      })}
    </div>
  );
  const sumBlock = (
    <>
      <div className="sum-row" style={{ marginTop: 12 }}>
        <span className="t-s muted">
          {n} {tx('koltuk · hizmet bedeli dahil', n === 1 ? 'seat · fees included' : 'seats · fees included')}
        </span>
        <span className="total">{tl(total, lang)}</span>
      </div>
      <button type="button" className="btn btn-primary btn-lg btn-block" style={{ marginTop: 12 }} onClick={holdSeats} disabled={!n || busy}>
        {busy ? tx('Koltuklar tutuluyor…', 'Holding seats…') : n ? tx('Devam et — koltukları tut', 'Continue — hold seats') : tx('Koltuk seç', 'Select seats')}
      </button>
    </>
  );

  return (
    <>
      <div className="seat-top" ref={topRef}>
        <button type="button" className="icon-btn" onClick={() => nav(`/e/${data.event.slug}?s=${sid}`)} aria-label={tx('Etkinliğe dön', 'Back to event')}>
          <Ico n="back" />
        </button>
        <div className="tt">
          <b>{title}</b>
          <span>
            {dayShort(data.session.startsAt, lang)} · {hhmm(data.session.startsAt)} · {data.venue.name}
          </span>
        </div>
        <span className={`badge ok live-badge ${live.connected ? '' : 'off'}`} title={live.connected ? tx('Koltuklar canlı güncelleniyor', 'Seats update live') : tx('Yeniden bağlanıyor', 'Reconnecting')}>
          <span className="pulse" /> {live.connected ? tx('Canlı', 'Live') : tx('Bağlanıyor', 'Connecting')}
        </span>
      </div>
      <div className="legend" aria-label={tx('Lejant', 'Legend')} ref={legendRef}>
        {(['loca', 'on', 'arka', 'balkon'] as const).map((k) => (
          <span className="li" key={k}>
            <span className="sw" style={{ background: TIERS[k].css }} />
            {tierName(k)} <span className="price">{tl(data.prices[k], lang)}</span>
          </span>
        ))}
        <span className="sep" />
        <span className="li">
          <span className="sw sel" />
          {tx('Seçili', 'Selected')}
        </span>
        <span className="li">
          <span className="sw held" />
          {tx('Tutuluyor', 'Held')}
        </span>
        <span className="li">
          <span className="sw sold" />
          {tx('Satıldı', 'Sold')}
        </span>
        <span className="li">
          <Ico n="wc" cls="ico-s" />
          {tx('Erişilebilir', 'Accessible')}
        </span>
      </div>
      <div className="map-vp" id="vp" ref={vpRef} aria-describedby="maphelp">
        <div className="map-canvas" ref={mcRef} onKeyDown={onKey}>
          <svg width={geo.W} height={geo.H} viewBox={`0 0 ${geo.W} ${geo.H}`} aria-label={`${data.venue.name} ${tx('koltuk haritası', 'seat map')}`} role="group">
            <defs>
              <pattern id="hatch" width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                <rect width="5" height="5" fill="#E9E3D7" />
                <rect width="2.2" height="5" fill="var(--s-held)" />
              </pattern>
            </defs>
            <g transform={`translate(${-geo.ox} ${-geo.oy})`}>
              <path className="stage-shape" d={STAGE.path} />
              <text className="stage-txt" x={STAGE.tx} y={STAGE.ty}>
                {tx('SAHNE', 'STAGE')}
              </text>
              {data.map.sections.map((sec) =>
                sec.rows > 1 ? (
                  <text key={sec.id} className="seclbl" x={sec.box.cx} y={sec.box.y0 - 12}>
                    {sec.name.toLocaleUpperCase('tr-TR')}
                  </text>
                ) : (
                  <g key={sec.id}>
                    <rect className="loca-box" x={sec.box.x0 - 8} y={sec.box.y0 - 8} width={sec.box.x1 - sec.box.x0 + 16} height={sec.box.y1 - sec.box.y0 + 16} rx="10" />
                    <text className="seclbl" x={sec.box.cx} y={sec.box.y0 - 16} style={{ fontSize: 10 }}>
                      {sec.name.toLocaleUpperCase('tr-TR')}
                    </text>
                  </g>
                ),
              )}
              {data.map.sections.flatMap((sec) =>
                sec.lbls.map((l, i) => (
                  <text key={sec.id + i} className="rowlbl" x={l.x.toFixed(1)} y={l.y.toFixed(1)}>
                    {l.t}
                  </text>
                )),
              )}
              {seats.map((s) => {
                const st = stateOf(s.id);
                const sel = selSet.has(s.id);
                const cls = sel ? 's-sel' : st === 'blocked' ? 's-sold' : `s-${st}`;
                const focusable = sel || st === 'avail';
                return (
                  <g
                    key={s.id}
                    className={`seat t-${s.tier} ${cls}${s.wc ? ' s-wc' : ''}${live.flash.has(s.id) ? ' flash' : ''}`}
                    data-seat={s.id}
                    transform={`translate(${s.x} ${s.y}) scale(${SEAT_SCALE})`}
                    tabIndex={focusable ? 0 : -1}
                    role="button"
                    aria-pressed={sel}
                    aria-disabled={!focusable}
                    aria-label={seatAria(s, st, sel)}
                  >
                    <rect className="b" x="-13" y="-12" width="26" height="24" rx="7" />
                    <rect className="ring" x="-16" y="-15" width="32" height="30" rx="9" />
                    <text>{s.num}</text>
                    {s.wc && <path className="wc" d={WC_PATH} />}
                    <path className="chk" d="M-5 0.5 l3.2 3.2 L5 -4" />
                  </g>
                );
              })}
              <text className="seclbl" x={STAGE.tx} y={geo.oy + geo.H - 18} style={{ fontSize: 10 }}>
                {tx('GİRİŞ · KAPI A (SOL) · KAPI B (SAĞ) · ASANSÖR', 'ENTRANCE · DOOR A (LEFT) · DOOR B (RIGHT) · LIFT')}
              </text>
            </g>
          </svg>
        </div>
      </div>
      <p id="maphelp" className="sr">
        {tx(
          'Koltuklar arasında Tab veya ok tuşlarıyla gezin, Enter ile seç. Haritayı sürükleyerek kaydır, iki parmakla veya fare tekerleğiyle yakınlaştır.',
          'Move between seats with Tab or the arrow keys, press Enter to select. Drag to pan, pinch or use the mouse wheel to zoom.',
        )}
      </p>
      <div className="pinch-hint" ref={hintRef}>
        {tx('İki parmakla yakınlaştır · sürükleyerek gez', 'Pinch to zoom · drag to move')}
      </div>
      <div className="zoom" ref={zoomRef}>
        <button type="button" className="icon-btn" onClick={() => zoomAt(1.4, vpRef.current!.clientWidth / 2, vpRef.current!.clientHeight / 2)} aria-label={tx('Yakınlaştır', 'Zoom in')}>
          <Ico n="plus" />
        </button>
        <button type="button" className="icon-btn" onClick={() => zoomAt(0.7, vpRef.current!.clientWidth / 2, vpRef.current!.clientHeight / 2)} aria-label={tx('Uzaklaştır', 'Zoom out')}>
          <Ico n="minus" />
        </button>
        <button type="button" className="icon-btn" onClick={fitMap} aria-label={tx('Tümünü göster', 'Fit map')}>
          <Ico n="fit" />
        </button>
      </div>
      <div className="dock seat-mobile-only" ref={dockRef}>
        <div className="sheet">
          <button type="button" className="grab" onClick={() => setSheetOpen(!sheetOpen)} aria-label={sheetOpen ? tx('Özeti daralt', 'Collapse summary') : tx('Özeti genişlet', 'Expand summary')} />
          {n ? (
            <>
              <button type="button" onClick={() => setSheetOpen(!sheetOpen)} style={{ all: 'unset', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'baseline' }}>
                <span style={{ fontWeight: 600 }}>
                  {n} {tx('koltuk seçildi', n === 1 ? 'seat selected' : 'seats selected')} <Ico n={sheetOpen ? 'down' : 'chev'} cls="ico-s" />
                </span>
                <span className="muted t-s">
                  {freeTotal} {tx('boş koltuk', 'seats free')}
                </span>
              </button>
              {sheetOpen && selList}
              {sumBlock}
            </>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontWeight: 600 }}>{tx('Koltuğunu seç', 'Pick your seat')}</span>
                <span className="muted t-s" style={{ textAlign: 'right' }}>
                  {countsTxt} {tx('boş', 'free')}
                </span>
              </div>
              <p className="t-s muted" style={{ margin: '6px 0 0' }}>
                {tx(`Bir koltuğa dokun. En fazla ${data.maxSeats} koltuk.`, `Tap a seat. Up to ${data.maxSeats} seats.`)}
              </p>
            </>
          )}
        </div>
      </div>
      <aside className="seat-side" aria-label={tx('Seçim özeti', 'Selection summary')}>
        <div style={{ padding: '24px 24px 0' }}>
          <div className="cap muted">{tx('Seçimin', 'Your selection')}</div>
          <div className="d-m" style={{ marginTop: 6 }}>
            {n ? `${n} ${tx('koltuk', n === 1 ? 'seat' : 'seats')}` : tx('Henüz koltuk yok', 'No seats yet')}
          </div>
          <div className="t-s muted" style={{ marginTop: 4 }}>
            {countsTxt} {tx('boş', 'free')}
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '12px 24px' }}>{selList}</div>
        <div style={{ padding: '16px 24px 24px', borderTop: '1px solid var(--rule)' }}>
          {sumBlock}
          <p className="t-s muted" style={{ margin: '12px 0 0', display: 'flex', gap: 8 }}>
            <Ico n="lock" cls="ico-s" /> {tx('Ödemeye geçince koltuklar 7 dakika senin.', 'Seats are yours for 7 minutes at checkout.')}
          </p>
        </div>
      </aside>
    </>
  );
}
